/**
 * Utility functions for SIE data handling
 */

/**
 * Amount unit type - controls how amounts are interpreted and formatted
 * - 'kr': Amounts are in kronor
 * - 'tkr': Amounts are in thousands of kronor (will be multiplied by 1000)
 * - 'auto': Automatic formatting (uses compact format for large amounts)
 */
export type AmountUnit = 'kr' | 'tkr' | 'auto';

export function formatSEK(amount: number): string {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatCompactSEK(amount: number): string {
  if (Math.abs(amount) >= 1000000) {
    return `${(amount / 1000000).toFixed(1)} Mkr`;
  } else if (Math.abs(amount) >= 1000) {
    return `${(amount / 1000).toFixed(0)} tkr`;
  }
  return formatSEK(amount);
}

/**
 * Display unit type for consistent formatting across a view
 */
export type DisplayUnit = 'tkr' | 'Mkr';

/**
 * Display format type - user-selectable format for amounts
 * - 'auto': Automatically use tkr or Mkr based on largest value in view
 * - 'kr': Display all amounts in kronor
 * - 'tkr': Display all amounts in thousands
 * - 'Mkr': Display all amounts in millions
 */
export type DisplayFormat = 'auto' | 'kr' | 'tkr' | 'Mkr';

/**
 * Determine the optimal display unit for a set of amounts.
 * Uses a consistent unit for all amounts based on the largest absolute value:
 * - If any amount >= 1M, use Mkr for all
 * - Otherwise use tkr for all
 * 
 * This prevents confusing mix of "800 tkr" and "1.2 Mkr" in the same view.
 */
export function determineOptimalUnit(amounts: number[]): DisplayUnit {
  if (!amounts.length) return 'tkr';
  const maxAbs = Math.max(...amounts.map(a => Math.abs(a)));
  return maxAbs >= 1000000 ? 'Mkr' : 'tkr';
}

/**
 * Format amount with a specified display unit (for consistent formatting).
 * All amounts in the same view should use the same unit.
 */
export function formatAmountWithUnit(amount: number, displayUnit: DisplayUnit): string {
  if (displayUnit === 'Mkr') {
    return `${(amount / 1000000).toFixed(1)} Mkr`;
  }
  return `${(amount / 1000).toFixed(0)} tkr`;
}

/**
 * Format amount with user-selected display format.
 * Supports 'auto' (uses autoUnit), 'kr', 'tkr', or 'Mkr'.
 */
export function formatAmountWithDisplayFormat(
  amount: number, 
  format: DisplayFormat,
  autoUnit?: DisplayUnit
): string {
  switch (format) {
    case 'kr':
      return formatSEK(amount);
    case 'tkr':
      return `${(amount / 1000).toFixed(0)} tkr`;
    case 'Mkr':
      return `${(amount / 1000000).toFixed(1)} Mkr`;
    case 'auto':
    default:
      return formatAmountWithUnit(amount, autoUnit || 'tkr');
  }
}

/**
 * Format amount based on selected unit
 * - kr: Display as kronor (raw value)
 * - tkr: Multiply by 1000 and display as kronor (assumes input is in tkr)
 * - auto: Use compact formatting (tkr for thousands, Mkr for millions)
 * 
 * NOTE: For consistent display across a view, use determineOptimalUnit() + formatAmountWithUnit()
 */
export function formatAmount(amount: number, unit: AmountUnit = 'auto'): string {
  if (unit === 'tkr') {
    // Input is in thousands, multiply to get kronor then format
    return formatSEK(amount * 1000);
  }
  if (unit === 'kr') {
    return formatSEK(amount);
  }
  // Auto mode - use compact formatting
  return formatCompactSEK(amount);
}

export function formatPercent(value: number): string {
  if (!isFinite(value)) return '-';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

export function formatPercentagePoints(value: number): string {
  if (!isFinite(value)) return '-';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}pp`;
}

export function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('sv-SE');
  } catch {
    return dateStr;
  }
}

export const MONTH_NAMES: Record<string, string> = {
  '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr',
  '05': 'Maj', '06': 'Jun', '07': 'Jul', '08': 'Aug',
  '09': 'Sep', '10': 'Okt', '11': 'Nov', '12': 'Dec',
};

export const MONTH_FULL_NAMES: Record<string, string> = {
  '01': 'Januari', '02': 'Februari', '03': 'Mars', '04': 'April',
  '05': 'Maj', '06': 'Juni', '07': 'Juli', '08': 'Augusti',
  '09': 'September', '10': 'Oktober', '11': 'November', '12': 'December',
};

export function getMonthShortName(month: string): string {
  const monthNum = month.split('-')[1];
  return MONTH_NAMES[monthNum] || monthNum;
}

export function getMonthFullName(month: string): string {
  const monthNum = month.split('-')[1];
  return MONTH_FULL_NAMES[monthNum] || monthNum;
}

export function calculatePercentChange(current: number, previous: number): number {
  if (previous === 0) {
    return current === 0 ? 0 : 100;
  }
  return ((current - previous) / Math.abs(previous)) * 100;
}

export function getChangeFlag(percentChange: number): 'normal' | 'warning' | 'critical' {
  const absChange = Math.abs(percentChange);
  if (absChange >= 25) return 'critical';
  if (absChange >= 10) return 'warning';
  return 'normal';
}
